import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../../schema.dexie";
import { getClinicaAtiva, listarLocais, listarMembros } from "../queries";
import {
  criarAgendamento,
  editarAgendamento,
  getAgendamento,
} from "../queries";
import { hojeChave } from "../domain";
import {
  MODALIDADE_LABEL,
  TIPO_LABEL,
  type Modalidade,
  type TipoConsulta,
} from "../types";

const TIPOS: TipoConsulta[] = ["avaliacao", "retorno", "sessao", "teleconsulta"];
const MODALIDADES: Modalidade[] = ["presencial", "online"];
const DURACOES = [30, 40, 50, 60, 90];

/** Criar ou editar um agendamento. `:id` presente = edição. */
export function NovoAgendamento() {
  const nav = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const editando = Boolean(id);

  const clinica = useLiveQuery(getClinicaAtiva);
  const clinicaId = clinica?.id;
  const membros = useLiveQuery(
    () => (clinicaId ? listarMembros(clinicaId) : Promise.resolve([])),
    [clinicaId],
  );
  const locais = useLiveQuery(
    () => (clinicaId ? listarLocais(clinicaId) : Promise.resolve([])),
    [clinicaId],
  );
  const criancas = useLiveQuery(() => db.crianca.toArray()) ?? [];

  // estado do formulário
  const [profissionalId, setProfissionalId] = useState("");
  const [pacienteRef, setPacienteRef] = useState(""); // crianca.id ou "" (avulso)
  const [pacienteNome, setPacienteNome] = useState("");
  const [pacienteContato, setPacienteContato] = useState("");
  const [data, setData] = useState(params.get("data") || hojeChave());
  const [hora, setHora] = useState("09:00");
  const [duracao, setDuracao] = useState(50);
  const [tipo, setTipo] = useState<TipoConsulta>("sessao");
  const [modalidade, setModalidade] = useState<Modalidade>("presencial");
  const [localId, setLocalId] = useState("");
  const [linkOnline, setLinkOnline] = useState("");
  const [obs, setObs] = useState("");
  const [carregado, setCarregado] = useState(false);

  // profissional padrão = 1º membro (na experiência solo, o dono)
  useEffect(() => {
    if (!profissionalId && membros && membros.length) setProfissionalId(membros[0].id);
  }, [membros, profissionalId]);

  // carrega valores ao editar
  useEffect(() => {
    if (!editando || !id || carregado) return;
    void getAgendamento(id).then((a) => {
      if (!a) return;
      setProfissionalId(a.profissional_id);
      setPacienteRef(a.paciente_id ?? "");
      setPacienteNome(a.paciente_nome);
      setPacienteContato(a.paciente_contato ?? "");
      setData(a.data);
      setHora(a.hora_inicio);
      setDuracao(a.duracao_min);
      setTipo(a.tipo);
      setModalidade(a.modalidade);
      setLocalId(a.local_id ?? "");
      setLinkOnline(a.link_online ?? "");
      setObs(a.observacao_agenda ?? "");
      setCarregado(true);
    });
  }, [editando, id, carregado]);

  function escolherPaciente(ref: string) {
    setPacienteRef(ref);
    if (ref) {
      const c = criancas.find((x) => x.id === ref);
      if (c) setPacienteNome(c.apelido);
    }
  }

  const nomeFinal = pacienteNome.trim();
  const podeSalvar = Boolean(clinicaId && profissionalId && nomeFinal && data && hora);

  async function salvar() {
    if (!podeSalvar || !clinicaId) return;
    const base = {
      profissional_id: profissionalId,
      paciente_id: pacienteRef || null,
      paciente_nome: nomeFinal,
      paciente_contato: pacienteContato || null,
      data,
      hora_inicio: hora,
      duracao_min: duracao,
      tipo,
      modalidade,
      local_id: modalidade === "online" ? null : localId || null,
      link_online: modalidade === "online" ? linkOnline || null : null,
      observacao_agenda: obs || null,
    };
    if (editando && id) {
      await editarAgendamento(id, base);
      nav(`/agenda/${id}`);
    } else {
      const novo = await criarAgendamento({ clinica_id: clinicaId, ...base });
      nav(`/agenda/${novo}`);
    }
  }

  return (
    <main className="tela">
      <button className="btn btn-fantasma" onClick={() => nav(-1)}>← voltar</button>
      <h1 className="tela-titulo">{editando ? "editar consulta" : "nova consulta"}</h1>

      <div className="card secao">
        <div className="campo">
          <label>paciente</label>
          <select value={pacienteRef} onChange={(e) => escolherPaciente(e.target.value)}>
            <option value="">outro (digitar nome)</option>
            {criancas.map((c) => (
              <option key={c.id} value={c.id}>{c.apelido}</option>
            ))}
          </select>
        </div>
        {!pacienteRef && (
          <div className="campo">
            <label>nome do paciente</label>
            <input
              value={pacienteNome}
              onChange={(e) => setPacienteNome(e.target.value)}
              placeholder="como aparece na agenda"
            />
          </div>
        )}
        <div className="campo">
          <label>contato para confirmar (opcional)</label>
          <input
            value={pacienteContato}
            onChange={(e) => setPacienteContato(e.target.value)}
            placeholder="telefone / whatsapp"
            inputMode="tel"
          />
        </div>
        <div className="campo">
          <label>profissional</label>
          <select value={profissionalId} onChange={(e) => setProfissionalId(e.target.value)}>
            {(membros ?? []).map((m) => (
              <option key={m.id} value={m.id}>{m.nome}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="card secao">
        <div className="linha-campos">
          <div className="campo" style={{ marginBottom: 0 }}>
            <label>data</label>
            <input type="date" value={data} onChange={(e) => setData(e.target.value)} />
          </div>
          <div className="campo" style={{ marginBottom: 0 }}>
            <label>hora</label>
            <input type="time" value={hora} onChange={(e) => setHora(e.target.value)} />
          </div>
        </div>
        <div className="campo" style={{ marginTop: "var(--s4)" }}>
          <label>duração</label>
          <select value={duracao} onChange={(e) => setDuracao(Number(e.target.value))}>
            {DURACOES.map((d) => (
              <option key={d} value={d}>{d} min</option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label>tipo</label>
          <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoConsulta)}>
            {TIPOS.map((t) => (
              <option key={t} value={t}>{TIPO_LABEL[t]}</option>
            ))}
          </select>
        </div>
        <div className="campo" style={{ marginBottom: 0 }}>
          <label>modalidade</label>
          <select value={modalidade} onChange={(e) => setModalidade(e.target.value as Modalidade)}>
            {MODALIDADES.map((m) => (
              <option key={m} value={m}>{MODALIDADE_LABEL[m]}</option>
            ))}
          </select>
        </div>
        {modalidade === "online" ? (
          <div className="campo" style={{ marginTop: "var(--s4)", marginBottom: 0 }}>
            <label>link da teleconsulta (opcional)</label>
            <input
              value={linkOnline}
              onChange={(e) => setLinkOnline(e.target.value)}
              placeholder="https://…"
              inputMode="url"
            />
          </div>
        ) : (
          (locais ?? []).length > 0 && (
            <div className="campo" style={{ marginTop: "var(--s4)", marginBottom: 0 }}>
              <label>consultório</label>
              <select value={localId} onChange={(e) => setLocalId(e.target.value)}>
                <option value="">—</option>
                {(locais ?? []).map((l) => (
                  <option key={l.id} value={l.id}>{l.nome}</option>
                ))}
              </select>
            </div>
          )
        )}
      </div>

      <div className="card secao">
        <div className="campo" style={{ marginBottom: 0 }}>
          <label>observação da agenda (opcional)</label>
          <textarea
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            placeholder="ex.: trazer exames. (administrativo — nunca prontuário)"
            rows={2}
          />
        </div>
      </div>

      <button
        className="btn btn-primario btn-bloco btn-grande"
        disabled={!podeSalvar}
        onClick={salvar}
      >
        {editando ? "salvar alterações" : "agendar consulta"}
      </button>
    </main>
  );
}
